import React from 'react';
import type { AgentSettingsConflict, AgentSettingsInvalid } from '@/types/agent-types-new';

/**
 * Shows what the settings API reported beside the values: (column, config) pairs
 * that differ, and recorded values that are not numbers. The column is what this
 * page reads and writes; the config keys are the older layout. Which one the runtime
 * reads is an open question (task-rqr8foednl), so nothing is resolved here: differing
 * sources are shown with their names and the API refuses to write over either, so Save
 * stays disabled; a malformed value is shown with
 * its source and Save stays disabled, so the page never writes over it silently.
 */
export function SettingsConflictNotice({ conflicts, invalid }: { conflicts?: AgentSettingsConflict[]; invalid?: AgentSettingsInvalid[] }) {
  const hasConflicts = !!conflicts && conflicts.length > 0;
  const hasInvalid = !!invalid && invalid.length > 0;
  if (!hasConflicts && !hasInvalid) return null;
  return (
    <>
      {hasInvalid && (
        <div className="p-3 mb-3 text-xs rounded-md bg-red-50 border border-red-200 text-red-900" role="alert">
          <div className="font-medium mb-1">Recorded settings that are not numbers</div>
          <ul className="list-disc ml-4 space-y-0.5">
            {invalid!.map((i) => (
              <li key={`${i.field}:${i.source}`}>
                <span className="font-mono">{i.field}</span>: <span className="font-mono">{i.source ?? 'unknown source'}</span> holds <span className="font-mono">{JSON.stringify(i.value)}</span>. Shown as not configured; saving is disabled until the recorded value is corrected at its source.
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasConflicts && (
        <div className="p-3 mb-3 text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900" role="alert">
          <div className="font-medium mb-1">Recorded settings disagree between their stores</div>
          <ul className="list-disc ml-4 space-y-0.5">
            {conflicts!.map((c) => (
              <li key={c.field}>
                <span className="font-mono">{c.field}</span>:{' '}
                {c.sources.map((s, i) => (
                  <React.Fragment key={s.source}>
                    {i > 0 ? ', ' : ''}<span className="font-mono">{s.source}</span> holds <span className="font-mono">{String(s.value)}</span>
                  </React.Fragment>
                ))}
                . This page shows <span className="font-mono">{c.sources[0]?.source}</span>; saving this field is disabled until the stores agree at their source.
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
